// Sugalarm — the kiosk No Data alarm's per-member outage keys (cycle 453.2).
//
// The dark-feed tone and its Silence are about ONE member's outage. Until
// cycle 453.2 both were held in member-blind slots in `useAlarmSounds`:
//   - the Silence stored only the newest reading's TIMESTAMP, so a second
//     member whose newest reading carried the same timestamp was muted by
//     the first member's Silence; and
//   - the play-once latch was a bare boolean, so it could not say WHICH
//     outage it had already sounded for.
//
// Both are now keyed by the newest reading's ROW ID. A glucose reading row
// belongs to exactly one member, so no other member's outage can ever match
// it, and a new outage always has a different newest reading, so a new
// outage always sounds (the cycle-360.5 rule). The id comes from the reading
// itself, never from a separately-tracked "active member", so the one render
// in which a member switch has landed but the old member's reading is still
// on screen cannot mis-key anything.
//
// Local state only: reading ids, nothing else, never persisted and never
// sent anywhere.

/** How many silenced outages are remembered. Far more than a household has
 *  members; the oldest entry drops first. */
export const NO_DATA_SILENCE_MEMORY = 16;

/** Silenced outages, oldest first: the newest-reading row id of each
 *  outage that was silenced. */
export type NoDataSilences = readonly string[];

/** The identity of one member's outage: the row id of that member's newest
 *  reading. Null when there is no reading. */
export function noDataOutageKey(
  reading: { id: string; timestamp?: string } | null | undefined,
): string | null {
  // Every server writer sends `id`; if one ever did not, fall back to the
  // timestamp so the tone still sounds (fail LOUD, never quiet).
  if (reading?.id) return reading.id;
  return reading?.timestamp ? `ts:${reading.timestamp}` : null;
}

/** Is this outage silenced? Only a Silence of THIS outage counts. */
export function noDataSilenced(silences: NoDataSilences, outageKey: string | null): boolean {
  return outageKey != null && silences.includes(outageKey);
}

/** Record a Silence for this outage, leaving every other entry as it was. */
export function withNoDataSilence(
  silences: NoDataSilences,
  outageKey: string | null,
): NoDataSilences {
  if (outageKey == null || silences.includes(outageKey)) return silences;
  return [...silences, outageKey].slice(-NO_DATA_SILENCE_MEMORY);
}

/** The play-once decision: the tone is due when an outage alarm is up and
 *  the tone has not already been started for exactly that outage. */
export function noDataCueDue(activeKey: string | null, playedKey: string | null): boolean {
  return activeKey != null && activeKey !== playedKey;
}
